/**
 * Spatialization (R26).
 *
 * A `PannerNode` per active source with distance attenuation, positioned from
 * world coordinates relative to the camera target. The listener moves with the
 * camera rather than with any unit, because the player's ear is where their
 * eye is.
 */

const REF_DISTANCE = 6
const MAX_DISTANCE = 42
const ROLLOFF = 1.4

export class Spatial {
  private listenerX = 0
  private listenerZ = 0

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly destination: AudioNode,
  ) {
    this.applyListener()
  }

  setListener(worldX: number, worldZ: number): void {
    this.listenerX = worldX
    this.listenerZ = worldZ
    this.applyListener()
  }

  private applyListener(): void {
    const l = this.ctx.listener
    // The modern AudioParam interface where available, the legacy setter
    // otherwise — Safari only grew the former recently.
    if (l.positionX) {
      l.positionX.value = this.listenerX
      l.positionY.value = 0
      l.positionZ.value = this.listenerZ
      if (l.forwardX) {
        l.forwardX.value = 0
        l.forwardY.value = -1
        l.forwardZ.value = 0
        l.upX.value = 0
        l.upY.value = 0
        l.upZ.value = -1
      }
    } else if (typeof (l as unknown as { setPosition?: unknown }).setPosition === 'function') {
      ;(l as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(
        this.listenerX,
        0,
        this.listenerZ,
      )
    }
  }

  /**
   * A panner at a world position, already connected to the master bus.
   *
   * Attenuation is inverse-law and clamped at `MAX_DISTANCE`, so a sound at the
   * far edge of the map fades to inaudible rather than cutting abruptly to
   * silence — an abrupt cut is far more noticeable than the sound itself.
   */
  place(worldX: number, worldZ: number): { panner: PannerNode; input: AudioNode } {
    const panner = this.ctx.createPanner()
    panner.panningModel = 'equalpower'
    panner.distanceModel = 'inverse'
    panner.refDistance = REF_DISTANCE
    panner.maxDistance = MAX_DISTANCE
    panner.rolloffFactor = ROLLOFF

    if (panner.positionX) {
      panner.positionX.value = worldX
      panner.positionY.value = 0
      panner.positionZ.value = worldZ
    } else if (typeof (panner as unknown as { setPosition?: unknown }).setPosition === 'function') {
      ;(panner as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(
        worldX,
        0,
        worldZ,
      )
    }

    panner.connect(this.destination)
    return { panner, input: panner }
  }

  /** Rough audibility test, so a distant event can skip synthesis entirely. */
  isAudible(worldX: number, worldZ: number): boolean {
    const dx = worldX - this.listenerX
    const dz = worldZ - this.listenerZ
    return dx * dx + dz * dz < MAX_DISTANCE * MAX_DISTANCE
  }
}
